const DEFAULT_TIMEOUT_MS = 5000;
const POLL_INTERVAL_MS = 100;

const hasLayoutBox = (element: HTMLElement): boolean => {
  const { width, height } = element.getBoundingClientRect();
  return width > 0 || height > 0;
};

const isValidSelector = (selector: string): boolean => {
  try {
    document.querySelectorAll(selector);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * First element matching `selector` with a non-zero layout box.
 */
export const findLaidOutElement = (selector: string): HTMLElement | null => {
  for (const element of document.querySelectorAll<HTMLElement>(selector)) {
    if (hasLayoutBox(element)) {
      return element;
    }
  }
  return null;
};

/**
 * Polls for the first laid-out match for `selector` until found, `timeoutMs`
 * elapses, or `signal` aborts; resolves `null` on timeout, abort, or an
 * invalid selector.
 */
export const waitForElement = (
  selector: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<HTMLElement | null> =>
  new Promise(resolve => {
    if (!isValidSelector(selector)) {
      resolve(null);
      return;
    }

    const existing = findLaidOutElement(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const deadline = performance.now() + timeoutMs;
    const tick = () => {
      if (signal?.aborted) {
        resolve(null);
        return;
      }
      const element = findLaidOutElement(selector);
      if (element) {
        resolve(element);
        return;
      }
      if (performance.now() >= deadline) {
        resolve(null);
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    setTimeout(tick, POLL_INTERVAL_MS);
  });
